import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  IconButton,
  Slider,
  Tooltip,
  Collapse,
  Typography
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Refresh as RestartIcon
} from '@mui/icons-material';

/**
 * Simplified inline audio player that appears next to messages
 */
const InlineAudioPlayer = ({
  audioSrc,
  isVisible = true, // Now always visible by default
  disabled = false, // New prop to control disabled state
  onClose,
  autoPlay = false
}) => {
  const audioRef = useRef(new Audio());
  const intervalRef = useRef(null);
  const isScrubbingRef = useRef(false);

  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0); // New state for speed

  // Set up audio when src changes
  useEffect(() => {
    const audio = audioRef.current;

    if (!audioSrc) return;

    // Clean up previous audio
    audio.pause();
    setIsPlaying(false);
    clearInterval(intervalRef.current);

    // Set up new audio
    audio.src = audioSrc;
    audio.volume = 0.8; // Slightly lower volume for inline player
    audio.playbackRate = playbackSpeed; // Set initial playback rate

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setCurrentTime(0);
      if (autoPlay && isVisible && !disabled) {
        audio.play().catch(error => console.error("Autoplay failed:", error));
        setIsPlaying(true);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
    };

    // Set up event listeners
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    // Clean up function
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioSrc, autoPlay, isVisible, disabled]);

  // Update playback speed when changed
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
    }
  }, [playbackSpeed]);

  // Set up interval to update current time
  useEffect(() => {
    if (isPlaying && !disabled) {
      intervalRef.current = setInterval(() => {
        // Only update if user is not actively scrubbing
        if (!isScrubbingRef.current) {
          setCurrentTime(audioRef.current.currentTime);
        }
      }, 100);
    } else {
      clearInterval(intervalRef.current);
    }

    return () => clearInterval(intervalRef.current);
  }, [isPlaying, disabled]);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      const audio = audioRef.current;
      audio.pause();
      audio.src = '';
      clearInterval(intervalRef.current);
    };
  }, []);

  // Stop audio when becoming invisible or disabled
  useEffect(() => {
    if (!isVisible || disabled) {
      const audio = audioRef.current;
      audio.pause();
      setIsPlaying(false);
    }
  }, [isVisible, disabled]);

  // Handle play/pause
  const togglePlayPause = () => {
    if (disabled) return;

    const audio = audioRef.current;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio.play().catch(error => console.error("Play failed:", error));
      setIsPlaying(true);
    }
  };

  // Handle slider interaction start
  const handleSliderMouseDown = () => {
    if (disabled) return;
    isScrubbingRef.current = true;
  };

  // Handle slider interaction end
  const handleSliderMouseUp = () => {
    if (disabled) return;
    // Small delay to ensure the final value is set
    setTimeout(() => {
      isScrubbingRef.current = false;
    }, 50);
  };

  // Handle time change (scrubbing)
  const handleTimeChange = (_, newValue) => {
    if (disabled) return;
    setCurrentTime(newValue);
    audioRef.current.currentTime = newValue;
  };

  // Handle restart
  const handleRestart = () => {
    if (disabled) return;
    audioRef.current.currentTime = 0;
    setCurrentTime(0);
    if (!isPlaying) {
      audioRef.current.play().catch(error => console.error("Play failed:", error));
      setIsPlaying(true);
    }
  };

  // Handle speed change
  const handleSpeedChange = (_, newValue) => {
    if (disabled) return;
    setPlaybackSpeed(newValue);
  };

  // Format time (seconds -> MM:SS)
  const formatTime = (time) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
  <Collapse in={isVisible} orientation="horizontal">
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5, // Increased back to 0.5
        backgroundColor: disabled ? 'action.disabledBackground' : 'background.paper',
        border: '1px solid',
        borderColor: disabled ? 'action.disabled' : 'divider',
        borderRadius: 1,
        boxShadow: disabled ? 0 : 1,
        minWidth: 280,
        maxWidth: 320,
        opacity: disabled ? 0.6 : 1,
        transition: 'opacity 0.3s ease, background-color 0.3s ease',
        minHeight: 40 // Minimum height instead of fixed
      }}
    >
      {/* Play/Pause button */}
      <Tooltip title={disabled ? "Loading audio..." : (isPlaying ? "Pause" : "Play")}>
        <span>
          <IconButton
            onClick={togglePlayPause}
            size="small"
            color="primary"
            disabled={disabled}
            aria-label={isPlaying ? "pause" : "play"}
            sx={{
              color: disabled ? 'action.disabled' : 'primary.main',
              p: 0.5 // Back to normal padding
            }}
          >
            {isPlaying ? <PauseIcon fontSize="small" /> : <PlayIcon fontSize="small" />}
          </IconButton>
        </span>
      </Tooltip>

      {/* Progress slider */}
      <Box sx={{
        flex: 1,
        mx: 0.5,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center' // Center align the progress section
      }}>
        <Slider
          size="small"
          value={currentTime}
          max={duration || 100}
          onChange={handleTimeChange}
          onMouseDown={handleSliderMouseDown}
          onMouseUp={handleSliderMouseUp}
          onTouchStart={handleSliderMouseDown}
          onTouchEnd={handleSliderMouseUp}
          disabled={disabled}
          aria-label="audio progress"
          sx={{
            color: disabled ? 'action.disabled' : 'primary.main',
            height: 3, // Slightly thicker
            '& .MuiSlider-thumb': {
              width: 8,
              height: 8,
            },
            '& .MuiSlider-track': {
              height: 3,
            },
            '& .MuiSlider-rail': {
              height: 3,
            }
          }}
        />
        {/* Time display */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          fontSize: '0.65rem', // Slightly larger
          color: disabled ? 'action.disabled' : 'text.secondary',
          mt: 0.25, // Positive margin to ensure visibility
          lineHeight: 1
        }}>
          <span>{formatTime(currentTime)}</span>
          <span>{formatTime(duration)}</span>
        </Box>
      </Box>

      {/* Speed control slider */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        minWidth: 45,
        height: '100%', // Take full height
        justifyContent: 'center' // Center the speed control
      }}>
        <Typography variant="caption" sx={{
          fontSize: '0.6rem',
          color: disabled ? 'action.disabled' : 'text.secondary',
          mr: 0.5
        }}>
          {playbackSpeed.toFixed(1)}x
        </Typography>
        <Slider
          size="small"
          value={playbackSpeed}
          min={0.5}
          max={2.0}
          step={0.1}
          onChange={handleSpeedChange}
          disabled={disabled}
          aria-label="playback speed"
          sx={{
            width: 30,
            color: disabled ? 'action.disabled' : 'secondary.main',
            '& .MuiSlider-thumb': {
              width: 8,
              height: 8,
            },
            '& .MuiSlider-track': {
              height: 3,
            },
            '& .MuiSlider-rail': {
              height: 3,
            }
          }}
        />
      </Box>

      {/* Restart button */}
      <Tooltip title={disabled ? "Loading..." : "Restart"}>
        <span>
          <IconButton
            onClick={handleRestart}
            size="small"
            disabled={disabled}
            aria-label="restart"
            sx={{
              color: disabled ? 'action.disabled' : 'text.secondary',
              p: 0.5 // Back to normal padding
            }}
          >
            <RestartIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  </Collapse>
);
};

export default InlineAudioPlayer;